import { FALLBACK_SEARCH_PAGE_PREFIX, FALLBACK_CATEGORY_LINK_PREFIX } from "./constants.js";
import { isBD } from "./businessType.lib.js";
const instantiateObserver = (handleIntersect, options) => {
  const observer = new IntersectionObserver(handleIntersect, options);
  return observer;
};
const cookieExists = (name) => {
  const encodeName = encodeURIComponent(name);
  const regexp = new RegExp(
    `(?:^${encodeName}|;\\s*${encodeName})=(.*?)(?:;|$)`,
    "g"
  );
  const exists = regexp.test(document.cookie);
  return exists;
};
const getCookie = (name) => {
  if (!cookieExists(name)) {
    return null;
  }
  const regexp = new RegExp(
    `(?:^${encodeURIComponent(name)}|;\\s*${encodeURIComponent(name)})=(.*?)(?:;|$)`,
    "g"
  );
  const result = regexp.exec(document.cookie);
  if (!result) {
    return null;
  }
  const cookieValue = decodeURIComponent(result[1]).replace("%27", "'");
  try {
    return JSON.parse(cookieValue);
  } catch (e) {
    console.error(`Error in getCookie: ${e}`);
    return cookieValue;
  }
};
const hasActiveMembership = (wcMemberValue, signedIn) => {
  const wcMemberArr = wcMemberValue.split(",");
  const isValidated = wcMemberArr[4] === "1";
  const isVerified = wcMemberArr[5] === "1";
  const isActive = wcMemberArr[6] === "E0002";
  return Boolean(signedIn && (isValidated || isVerified) && isActive);
};
const hasMembershipId = (membershipIdCookieValue, signedIn, isProductDetailsPage) => {
  if (isBD()) {
    return Boolean((membershipIdCookieValue == null ? void 0 : membershipIdCookieValue.split(",")[1]) === "1");
  } else if (isProductDetailsPage) {
    return hasActiveMembership(membershipIdCookieValue, signedIn);
  } else {
    return Boolean((membershipIdCookieValue == null ? void 0 : membershipIdCookieValue.split(",")[4]) === "1");
  }
};
const orderCookie = (cookie) => {
  const ordered = Object.keys(cookie).sort().reduce((obj, key) => {
    return { ...obj, [key]: cookie[key] };
  }, {});
  return ordered;
};
const listMissingVariables = (variables) => {
  const missing = [];
  for (const name in variables) {
    if (variables[name] === void 0) {
      missing.push(name);
    }
  }
  return missing.join(", ");
};
const isValidUrl = (urlString) => {
  try {
    return Boolean(new URL(urlString || ""));
  } catch {
    return false;
  }
};
const getLanguage = (locale) => {
  const lang = locale && locale.includes("-") ? locale.split("-") : [];
  return lang.length > 0 && lang[0] ? lang[0] : "en";
};
const getLanguageName = (locale) => {
  const languageCode = getLanguage(locale);
  const displayNames = new Intl.DisplayNames([languageCode], {
    type: "language"
  });
  return displayNames.of(languageCode);
};
const createUUID = () => {
  let dt = (/* @__PURE__ */ new Date()).getTime();
  const uuid = "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(
    /[xy]/g,
    function(c) {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === "x" ? r : r & 3 | 8).toString(16);
    }
  );
  return uuid;
};
const isAbsoluteUrl = (url) => {
  return url == null ? void 0 : url.startsWith("http");
};
function modernizeCategoryLink(categoryPath, link) {
  if (!link || link === FALLBACK_CATEGORY_LINK_PREFIX) {
    return "/";
  }
  if (isAbsoluteUrl(link)) {
    return link;
  }
  if (link.startsWith(FALLBACK_SEARCH_PAGE_PREFIX)) {
    return link;
  }
  if (link.startsWith(FALLBACK_CATEGORY_LINK_PREFIX)) {
    return link;
  }
  if (!categoryPath) {
    return link;
  }
  if (link.startsWith("/")) {
    return `${categoryPath}${link.slice(1)}`;
  }
  return `${categoryPath}${link}`;
}
function isValidCategoryLink(link) {
  if (!link || link === "/" || link === "") return false;
  if (link.startsWith(FALLBACK_SEARCH_PAGE_PREFIX)) return false;
  const validCategoryPattern = /^\/[a-z]+\/$/;
  return validCategoryPattern.test(link);
}
function normalizeCategoryLink(categoryPath, link, publishedCategories) {
  if (!link) {
    return "/";
  }
  if (isAbsoluteUrl(link) || link.startsWith(FALLBACK_SEARCH_PAGE_PREFIX)) {
    return link;
  }
  const [baseLink, queryParams] = link.split("?") ?? [link, void 0];
  const queryString = queryParams ? `?${queryParams}` : "";
  if (!baseLink || baseLink === categoryPath || baseLink === "/") {
    return "/";
  }
  let validatedCategoryPath = FALLBACK_CATEGORY_LINK_PREFIX;
  if (isValidCategoryLink(categoryPath)) {
    validatedCategoryPath = categoryPath;
  } else {
    console.warn(
      `normalizeCategoryLink: Invalid CATEGORY_PATH_PREFIX environment variable ${categoryPath} provided. Please check your environment variables.`
    );
  }
  const normalizedLink = baseLink.replace(categoryPath || "", "").replace(".html", "").replace(/^\/+/, "");
  if (publishedCategories == null ? void 0 : publishedCategories.includes(normalizedLink)) {
    return `${validatedCategoryPath || ""}${normalizedLink}.html${queryString}`;
  } else {
    return link;
  }
}
const isNullOrEmpty = (val) => val === null || val === void 0 || val === "" || Array.isArray(val) && val.length === 0 || Object.keys(val).length === 0 && val.constructor === Object;
const encodeObjectToBase64 = (value) => {
  if (!value) {
    return null;
  }
  try {
    const jsonString = JSON.stringify(value);
    return btoa(jsonString);
  } catch (error) {
    console.warn("Failed to encode object to base64:", error);
    return null;
  }
};
const decodeObjectFromBase64 = (encodedValue) => {
  if (!encodedValue) {
    return null;
  }
  try {
    const jsonString = atob(encodedValue);
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn("Failed to decode object from base64:", error);
    return null;
  }
};
export {
  createUUID,
  decodeObjectFromBase64,
  encodeObjectToBase64,
  getCookie,
  getLanguage,
  getLanguageName,
  hasActiveMembership,
  hasMembershipId,
  instantiateObserver,
  isAbsoluteUrl,
  isNullOrEmpty,
  isValidCategoryLink,
  isValidUrl,
  listMissingVariables,
  modernizeCategoryLink,
  normalizeCategoryLink,
  orderCookie
};
